/**
 * The dispatches of a purchase order as a table (batch 8 T12c1, #3033): state only, never content. Cursor pages are loaded on request,
 * only so many are ever held, and a list that has more than that says so instead of pretending to be complete.
 */

import {
  Alert,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PagedRead } from '@/hooks/useProcurementDispatch';
import { DispatchStatusChip } from './DispatchStatus';
import { Freshness, LoadingBlock, ReadError } from './ReadFeedback';
import { describeDispatchError } from './dispatchErrors';
import { formatDateTimeZone } from './dispatchFormat';
import { LIST_PAGE_SIZE, MAX_LOADED_PAGES } from './dispatchApi';
import type { DispatchSummary } from './dispatchTypes';

interface DispatchListProps {
  read: PagedRead<DispatchSummary>;
  onOpen: (summary: DispatchSummary) => void;
}

export function DispatchList({ read, onOpen }: DispatchListProps) {
  const { t, i18n } = useTranslation('procurementDispatch');

  if (read.isLoading) return <LoadingBlock label={t('list.loading')} />;
  if (read.isError && read.items.length === 0)
    return <ReadError error={read.error} onRetry={read.refetch} />;

  return (
    <Box>
      <Freshness
        updatedAt={read.updatedAt}
        isStale={read.isStale}
        isRefetching={read.isRefetching}
        error={read.error}
        onRefresh={read.refetch}
      />
      {read.items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {t('list.empty')}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, overflowX: 'auto' }}>
          <Table size="small" aria-label={t('list.tableLabel')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('list.columns.requested')}</TableCell>
                <TableCell>{t('list.columns.channel')}</TableCell>
                <TableCell>{t('list.columns.status')}</TableCell>
                <TableCell align="right">{t('list.columns.version')}</TableCell>
                <TableCell align="right">{t('list.columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {read.items.map((summary) => (
                <TableRow key={summary.id} hover>
                  <TableCell>{formatDateTimeZone(summary.requestedAt, i18n.language)}</TableCell>
                  <TableCell>{t(`channel.${summary.channel}`)}</TableCell>
                  <TableCell>
                    <DispatchStatusChip status={summary.status} />
                    {summary.reconciliationRequired && (
                      <Typography variant="caption" color="warning.main" display="block">
                        {t('list.needsAttention')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{summary.documentVersion}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() => onOpen(summary)}
                      aria-label={t('list.openDetails', {
                        time: formatDateTimeZone(summary.requestedAt, i18n.language),
                      })}
                    >
                      {t('list.details')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {read.isFetchNextPageError && (
        <Alert severity="error" role="alert" sx={{ mt: 1 }}>
          {describeDispatchError(t, read.error)}
        </Alert>
      )}
      {read.hasNextPage && (
        <Button sx={{ mt: 1 }} onClick={read.fetchNextPage} disabled={read.isFetchingNextPage}>
          {read.isFetchingNextPage ? t('list.loadingMore') : t('list.showMore')}
        </Button>
      )}
      {read.overflow && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t('list.overflow', { max: MAX_LOADED_PAGES * LIST_PAGE_SIZE })}
        </Alert>
      )}
    </Box>
  );
}
